import React, { useState, useEffect } from 'react';
import { fetchTournamentMatches } from '../../api/TournamentsApi';
import { claimTournamentReward } from '../../api/PlayerDataApi';

const TournamentCard = ({
	tournament,
	playerRegistrations,
	formatTournamentDate,
	getDaysRemaining,
	openParticipantsModal,
	openChatModal,
	openSoloRegistration,
	openTeamRegistration,
	openStagesModal,
	currentPlayerId, // Pass the current player's ID
}) => {
	// Add state for the blinking indicator
	const [isBlinking, setIsBlinking] = useState(true);
	const [nextMatch, setNextMatch] = useState(null);
	const [matchLoading, setMatchLoading] = useState(false);
	const [isClaiming, setIsClaiming] = useState(false);

	// Blinking effect using useEffect
	useEffect(() => {
		const blinkInterval = setInterval(() => {
			setIsBlinking((prev) => !prev);
		}, 1000); // Toggle every second

		return () => clearInterval(blinkInterval);
	}, []);

	// Fetch match details for registered tournaments
	useEffect(() => {
		const fetchMatchDetails = async () => {
			if (!tournament.is_registered) return;

			try {
				setMatchLoading(true);
				const matchesData = await fetchTournamentMatches(tournament.id);

				// Find the next scheduled or ongoing match
				const upcomingMatch = matchesData.find(
					(match) => match.match_status === 'scheduled' || match.match_status === 'ongoing'
				);

				if (upcomingMatch) {
					setNextMatch(upcomingMatch);
				}
			} catch (error) {
				console.error('Error fetching match details:', error);
			} finally {
				setMatchLoading(false);
			}
		};

		fetchMatchDetails();
	}, [tournament.id, tournament.is_registered]);

	// Handle double click on card
	const handleDoubleClick = (event) => {
		// Prevent opening stages if clicking on buttons or interactive elements
		if (event.target.closest('button') || event.target.tagName === 'A' || event.target.tagName === 'INPUT') {
			return;
		}

		// Open stages modal
		if (typeof openStagesModal === 'function') {
			// // console.log("Double-click opening stages for:", tournament.id, tournament.name);
			openStagesModal(tournament.id, tournament.name);
		}
	};

	// Function to handle stages button click with explicit console logging
	const handleStagesButtonClick = (e) => {
		// Stop propagation to prevent parent elements from receiving the event
		e.stopPropagation();

		// // console.log("Stages button clicked for tournament:", tournament.id, tournament.name);
		// // console.log("openStagesModal function type:", typeof openStagesModal);
		// // console.log("openStagesModal prop value:", openStagesModal);

		if (typeof openStagesModal === 'function') {
			// // console.log("About to call openStagesModal with args:", tournament.id, tournament.name);
			openStagesModal(tournament.id, tournament.name);
			// // console.log("Called openStagesModal");
		} else {
			// console.warn('Tournament stages function not available', { openStagesModal });
		}
	};

	const getTournamentTypeBadge = (type) => {
		switch (type) {
			case 'solo':
				return 'bg-purple-100 text-purple-800 border border-purple-200';
			case 'duo':
				return 'bg-blue-100 text-blue-800 border border-blue-200';
			case 'squad':
				return 'bg-indigo-100 text-indigo-800 border border-indigo-200';
			default:
				return 'bg-gray-100 text-gray-800 border border-gray-200';
		}
	};

	const getTournamentStatusBadge = (status) => {
		switch (status) {
			case 'upcoming':
				return 'bg-blue-100 text-blue-800 border border-blue-200';
			case 'ongoing':
				return 'bg-green-100 text-green-800 border border-green-200';
			case 'completed':
				return 'bg-gray-100 text-gray-800 border border-gray-200';
			case 'registration_open':
				return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
			default:
				return 'bg-gray-100 text-gray-800 border border-gray-200';
		}
	};

	const formatEntryFee = (entryFeeType, entryFees) => {
		if (entryFeeType === 'free') {
			return (
				<>
					Free Entry <span className="text-yellow-500">🪙</span>
				</>
			);
		} else if (entryFeeType === 'coins') {
			return (
				<>
					{parseFloat(entryFees).toLocaleString()} Coins <span className="text-yellow-500">🪙</span>
				</>
			);
		} else {
			return `₹${parseFloat(entryFees).toLocaleString()}`;
		}
	};

	const formatPrize = (prizeCoin, prizeMoney) => {
		// Create an array to hold prize elements
		const elements = [];
		
		if (prizeCoin && parseFloat(prizeCoin) > 0) {
			elements.push(
				<span key="coins" className="inline-flex items-center">
					{parseFloat(prizeCoin).toLocaleString()}<span className="text-yellow-500 ml-1">🪙</span>
				</span>
			);
		}
		
		if (prizeMoney && parseFloat(prizeMoney) > 0) {
			elements.push(
				<span key="money" className="inline-flex items-center">
					₹{parseFloat(prizeMoney).toLocaleString()} <span className="text-green-500 ml-1">💵</span>
				</span>
			);
		}
		
		// Return the elements with proper separators
		return (
			<>
				{elements.map((element, index) => (
					<React.Fragment key={`prize-${index}`}>
						{index > 0 && <span className="mx-1">+</span>}
						{element}
					</React.Fragment>
				))}
			</>
		);
	};

	// Helper to format match time in 12-hour format
	const formatMatchTime = (timeString) => {
		if (!timeString) return '';
		const [hours, minutes] = timeString.split(':');
		const hour = parseInt(hours, 10);
		const ampm = hour >= 12 ? 'PM' : 'AM';
		const formattedHour = hour % 12 || 12;
		return `${formattedHour}:${minutes} ${ampm}`;
	};

	// Handle registration button click based on tournament type
	const handleRegistrationClick = (e) => {
		// Stop event propagation to prevent double-click handler from interfering
		e.stopPropagation();
		
		// Log to verify function is being called
		console.log("Registration button clicked for:", tournament.id, tournament.name);
		console.log("Tournament type:", tournament.tournamentType || tournament.tournament_type);
		
		// Check for proper handlers and call the appropriate one
		if (tournament.tournamentType === 'solo' || tournament.tournament_type === 'solo') {
			if (typeof openSoloRegistration === 'function') {
				console.log("Opening solo registration");
				openSoloRegistration(tournament.id);
			} else {
				console.error("openSoloRegistration function not available");
			}
		} else if (['squad', 'duo', 'team'].includes(tournament.tournamentType || tournament.tournament_type)) {
			if (typeof openTeamRegistration === 'function') {
				console.log("Opening team registration");
				openTeamRegistration(tournament.id, tournament.game);
			} else {
				console.error("openTeamRegistration function not available");
			}
		}
	};

	// Claim reward handler
	const handleClaimReward = async () => {
		try {
			setIsClaiming(true);
			const response = await claimTournamentReward(tournament.id);
			alert(response.message); // Show success message
			// Update wallet balances dynamically
			const playerData = JSON.parse(localStorage.getItem('player_data') || '{}');
			playerData.coin_balance = response.new_balances.coin_balance;
			playerData.total_balance = response.new_balances.total_money_balance;
			playerData.total_balance_withdrawable = response.new_balances.money_balance_withdrawable;
			localStorage.setItem('player_data', JSON.stringify(playerData));
			window.location.reload(); // Refresh the page to show updated balances
		} catch (error) {
			alert('Failed to claim reward. Please try again later.');
		} finally {
			setIsClaiming(false);
		}
	};

	// Add console logging to debug the winner check
	useEffect(() => {
		if (tournament.tournamentStatus === 'completed' || tournament.tournament_status === 'completed') {
			const winners = tournament.winnerPlayers || tournament.winner_players || [];
			// console.log('Tournament winners:', winners);
			// console.log('Current player ID:', currentPlayerId);
			// Check if current player is in winners by user_id
			const isPlayerWinner = Array.isArray(winners) && winners.some(winner => 
				typeof winner === 'object' ? winner.user_id === currentPlayerId : winner === currentPlayerId
			);
			// console.log('Is player a winner:', isPlayerWinner);
		}
	}, [tournament, currentPlayerId]);

	// Function to check if current player is a winner
	const isCurrentPlayerWinner = () => {
		const winners = tournament.winnerPlayers || tournament.winner_players || [];
		if (!Array.isArray(winners) || !currentPlayerId) return false;
		
		return winners.some(winner => 
			typeof winner === 'object' ? winner.user_id === currentPlayerId : winner === currentPlayerId
		);
	};

	// Function to get truncated email
	const getTruncatedEmail = (email) => {
		if (!email) return '';
		const parts = email.split('@');
		return parts[0];
	};

	// Add a state to track which winner items are expanded
	const [expandedWinners, setExpandedWinners] = useState({});
	
	// Toggle winner dropdown
	const toggleWinnerDropdown = (winnerId) => {
		setExpandedWinners(prev => ({
			...prev,
			[winnerId]: !prev[winnerId]
		}));
	};

	// Helper function to calculate registration percentage
	const getRegistrationPercentage = (tournament) => {
		// Determine if it's a team-based tournament
		const isTeamBased = ['squad', 'duo', 'team'].includes(
			tournament.tournamentType || tournament.tournament_type
		);
		
		// Get max slots
		const maxSlots = tournament.maxSlots || tournament.max_slots || 100;
		
		// Get registered count based on tournament type
		let registeredCount = 0;
		if (isTeamBased) {
			// For team tournaments, use total_teams_registered
			registeredCount = tournament.totalTeamsRegistered || tournament.total_teams_registered || 0;
		} else {
			// For solo tournaments, use total_players_registered
			registeredCount = tournament.totalPlayersRegistered || tournament.total_players_registered || 0;
		}
		
		// Calculate percentage
		return (registeredCount / maxSlots) * 100;
	};

	// Add a helper function to check registration status
	const isRegistered = () => {
		// Check both possible property names from API
		return tournament.is_registered === true || tournament.isRegistered === true;
	};

	// Add a new state for the notification dot blinking
	const [isChatDotBlinking, setChatDotBlinking] = useState(true);

	// Add a blinking effect for the chat notification dot
	useEffect(() => {
		const chatBlinkInterval = setInterval(() => {
			setChatDotBlinking((prev) => !prev);
		}, 800); // Slightly faster blink than the other elements to catch attention

		return () => clearInterval(chatBlinkInterval);
	}, []);

	return (
		<div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow border border-gray-200 relative">
			{/* Sponsor Section */}
			<div className="p-4 bg-gray-50 border-b border-gray-200">
				<h4 className="text-xs font-medium text-gray-700 mb-2">Sponsored By:</h4>
				<div className="flex justify-center items-center">
					<img src="images/nxt.png" alt="Sponsor" className="w-full h-32 object-cover rounded-md" />
				</div>
			</div>

			<div
				className={`p-4 border-b ${
					tournament.game.toLowerCase().includes('freefire') || tournament.game.toLowerCase().includes('free fire')
						? 'bg-gradient-to-r from-amber-50 to-amber-100'
						: tournament.game.toLowerCase().includes('pubg')
						? 'bg-gradient-to-r from-emerald-50 to-emerald-100'
						: tournament.game.toLowerCase().includes('call of duty') || tournament.game.toLowerCase().includes('cod')
						? 'bg-gradient-to-r from-red-50 to-red-100'
						: 'bg-gradient-to-r from-blue-50 to-blue-100'
				}`}
			>
				<div className="flex justify-between mb-2">
					<span
						className={`text-xs font-medium px-2.5 py-0.5 rounded-full capitalize shadow-sm ${getTournamentTypeBadge(
							tournament.tournamentType || tournament.tournament_type
						)}`}
					>
						{tournament.tournamentType || tournament.tournament_type}
					</span>
					<div className="flex items-center space-x-1">
						{/* Show reward claimed badge if applicable */}
						{(tournament.tournamentStatus === 'completed' || tournament.tournament_status === 'completed') && 
						 isCurrentPlayerWinner() &&
						 (tournament.rewardsClaimed || tournament.rewards_claimed) && (
							<span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-200 flex items-center">
								<svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
								</svg>
								Reward Claimed
							</span>
						)}
						<span
							className={`text-xs font-medium px-2.5 py-0.5 rounded-full capitalize shadow-sm ${getTournamentStatusBadge(
								tournament.tournamentStatus || tournament.tournament_status
							)}`}
						>
							{(tournament.tournamentStatus || tournament.tournament_status || "Unknown Status").replace('_', ' ')}
						</span>
					</div>
				</div>

				<div className="flex justify-between items-start">
					<h3 className="font-bold text-gray-800 text-lg mb-1 line-clamp-1">{tournament.name}</h3>

					{/* Non-blinking stages button with consistent styling */}
					<button
						onClick={handleStagesButtonClick}
						className="ml-2 mt-1 flex items-center justify-center w-8 h-8 rounded-full bg-yellow-500 text-white shadow-md hover:bg-yellow-600 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500"
						title="View Tournament Stages (Double-click card for stages)"
					>
						<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
						</svg>
					</button>
				</div>

				{/* Small hint text for double-click */}
				<div className="mt-1 mb-1 text-[10px] text-gray-500 italic text-right">Double-click for stages</div>

				<div className="flex items-center text-xs text-gray-600 mt-1">
					<span className="inline-flex items-center capitalize">
						{tournament.game.toLowerCase().includes('freefire') || tournament.game.toLowerCase().includes('free fire') ? (
							<span className="mr-1">🔥</span>
						) : tournament.game.toLowerCase().includes('pubg') ? (
							<span className="mr-1">🔫</span>
						) : tournament.game.toLowerCase().includes('call of duty') || tournament.game.toLowerCase().includes('cod') ? (
							<span className="mr-1">🎖️</span>
						) : (
							<span className="mr-1">🎮</span>
						)}
						{tournament.game}
					</span>
					<span className="mx-1.5">•</span>
					<span className="inline-flex items-center">
						<svg className="w-3 h-3 mr-1 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
						</svg>
						{/* Handle potential invalid date */}
						{tournament.tentativeHappeningDate || tournament.tentative_happening_date ? 
							formatTournamentDate(tournament.tentativeHappeningDate || tournament.tentative_happening_date) : 
							"Date TBD"}
					</span>
				</div>
			</div>

			<div className="p-4">
				<div className="grid grid-cols-2 gap-3 mb-4">
					<div className="bg-gradient-to-r from-primary-50 to-primary-50 p-2.5 rounded-md border border-primary-100">
						<div className="flex items-center mb-1 text-primary-700">
							<p className="text-xs font-medium">Prize Pool</p>
						</div>
						<p className="font-bold text-primary-700">
							{formatPrize(
								tournament.prizeCoin || tournament.prize_coin,
								tournament.prizeMoney || tournament.prize_money
							)}
						</p>
					</div>

					<div className="bg-gradient-to-r from-gray-50 to-gray-100 p-2.5 rounded-md border border-gray-200">
						<div className="flex items-center mb-1 text-gray-700">
							<p className="text-xs font-medium">Entry Fee</p>
						</div>
						<p className="font-bold text-gray-700">
							{formatEntryFee(
								tournament.entryFeeType || tournament.entry_fee_type,
								tournament.entryFees || tournament.entry_fees
							)}
						</p>
					</div>
				</div>

				<div className="mb-4">
					<div className="flex justify-between items-center mb-1.5">
						<p className="text-xs font-medium text-gray-700">
							Registration {getDaysRemaining(tournament.registrationEndDate || tournament.registration_end_date)}
						</p>
						<p className="text-xs text-gray-500">
							<span className="font-medium text-primary-600">
								{(['squad', 'duo', 'team'].includes(tournament.tournamentType || tournament.tournament_type)) 
									? (tournament.totalTeamsRegistered || tournament.total_teams_registered || 0)
									: (tournament.totalPlayersRegistered || tournament.total_players_registered || 0)
								}
							</span>
							/{tournament.maxSlots || tournament.max_slots} 
							{(['squad', 'duo', 'team'].includes(tournament.tournamentType || tournament.tournament_type)) 
								? ' teams' 
								: ' players'
							}
						</p>
					</div>
					<div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
						<div
							className={`h-2.5 rounded-full ${
								// Calculate ratio based on tournament type
								(getRegistrationPercentage(tournament) > 80)
									? 'bg-red-600'
									: (getRegistrationPercentage(tournament) > 50)
									? 'bg-yellow-500'
									: 'bg-primary-600'
							}`}
							style={{
								width: `${Math.min(getRegistrationPercentage(tournament), 100)}%`,
							}}
						></div>
					</div>
				</div>

				{tournament.is_registered && (
					<div className="bg-green-50 border border-green-200 rounded-md p-2.5 mb-4">
						<div className="flex items-center text-green-700 text-xs mb-1">
							<svg className="h-5 w-5 mr-1.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
							<span className="font-medium">Your Registration Details</span>
						</div>

						{playerRegistrations[tournament.id] && (
							<div className="mt-1 grid grid-cols-2 gap-x-1 text-xs text-gray-700">
								<div className="font-medium">Game ID:</div>
								<div>{playerRegistrations[tournament.id].game_id}</div>
								<div className="font-medium">Username:</div>
								<div>{playerRegistrations[tournament.id].game_username}</div>
							</div>
						)}

						{/* NEW: Match Details Section */}
						{matchLoading ? (
							<div className="mt-2 text-center py-2">
								<div className="animate-pulse text-xs text-gray-500">Loading match details...</div>
							</div>
						) : nextMatch ? (
							<div className="mt-2 pt-2 border-t border-green-200">
								<div className="font-medium text-xs text-green-700 mb-1">Next Match Information:</div>

								<div className="grid grid-cols-2 gap-x-1 text-xs text-gray-700">
									<div className="font-medium">Date:</div>
									<div>{new Date(nextMatch.match_date).toLocaleDateString()}</div>

									<div className="font-medium">Time:</div>
									<div>{formatMatchTime(nextMatch.match_time)}</div>

									{nextMatch.match_id && (
										<>
											<div className="font-medium">Match ID:</div>
											<div className="flex items-center">
												<span className="font-mono">{nextMatch.match_id}</span>
												<button
													onClick={(e) => {
														e.stopPropagation();
														navigator.clipboard.writeText(nextMatch.match_id);
													}}
													className="ml-1 text-gray-500 hover:text-gray-700"
													title="Copy ID"
												>
													<svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
													</svg>
												</button>
											</div>
										</>
									)}

									{nextMatch.match_password && (
										<>
											<div className="font-medium">Password:</div>
											<div className="flex items-center">
												<span className="font-mono">{nextMatch.match_password}</span>
												<button
													onClick={(e) => {
														e.stopPropagation();
														navigator.clipboard.writeText(nextMatch.match_password);
													}}
													className="ml-1 text-gray-500 hover:text-gray-700"
													title="Copy Password"
												>
													<svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
													</svg>
												</button>
											</div>
										</>
									)}
								</div>

								{!nextMatch.match_id && !nextMatch.match_password && (
									<div className="mt-1 text-xs text-amber-600 italic">
										Match ID & password will be available here 30 minutes before the match starts
									</div>
								)}

								{/* Match Link */}
								{nextMatch.match_link && (
									<a
										href={nextMatch.match_link}
										target="_blank"
										rel="noopener noreferrer"
										onClick={(e) => e.stopPropagation()}
										className="mt-2 block text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-center"
									>
										Join Match Room
									</a>
								)}

								{/* Livestream Link */}
								{nextMatch.match_livestream_link && (
									<a
										href={nextMatch.match_livestream_link}
										target="_blank"
										rel="noopener noreferrer"
										onClick={(e) => e.stopPropagation()}
										className="mt-2 flex items-center justify-center text-xs font-medium bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded"
									>
										<svg className="w-3.5 h-3.5 mr-1" viewBox="0 0 24 24" fill="currentColor">
											<path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
										</svg>
										Watch Livestream
									</a>
								)}
							</div>
						) : (
							<div className="mt-2 pt-2 border-t border-green-200 text-xs text-gray-600 italic">
								No upcoming matches found. Check back later for match details.
							</div>
						)}
					</div>
				)}

				{isRegistered() ? (
					// Remove the redundant "You're Registered" bar and just show the action buttons
					<div className="grid grid-cols-3 gap-1 relative z-20">
						<button
							onClick={() => openParticipantsModal(tournament.id, tournament.tournamentType || tournament.tournament_type)}
							className="px-2 py-1.5 bg-primary-100 border border-primary-200 text-primary-700 text-xs font-medium rounded-md hover:bg-primary-200 transition flex items-center justify-center"
						>
							<svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
							</svg>
							{/* Change text based on tournament type */}
							{['solo'].includes(tournament.tournamentType || tournament.tournament_type) ? 'Players' : 'Teams'}
						</button>
						<button
							onClick={() => openChatModal(tournament)}
							className="px-2 py-1.5 bg-green-50 border border-green-200 text-green-700 text-xs font-medium rounded-md hover:bg-green-100 transition flex items-center justify-center relative"
						>
							{/* Red notification dot */}
							<span className={`absolute -top-1 -right-1 h-3 w-3 rounded-full ${isChatDotBlinking ? 'bg-red-500' : 'bg-red-400'} border border-white`}></span>
							<svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
							</svg>
							Chat
						</button>
						<button
							className="px-2 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium rounded-md hover:bg-blue-100 transition flex items-center justify-center"
							disabled
						>
							<svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
							</svg>
							Registered
						</button>
					</div>
				) : (
					<div className="flex gap-2 pt-1 relative z-20">
						<div className="flex-1 flex gap-1">
							<button
								onClick={() => openParticipantsModal(tournament.id, tournament.tournamentType || tournament.tournament_type)}
								className="flex-1 px-3 py-2 bg-primary-50 border border-primary-200 text-primary-700 text-xs font-medium rounded-md hover:bg-primary-100 transition flex items-center justify-center"
							>
								<svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
								</svg>
								{/* Change text based on tournament type */}
								{['solo'].includes(tournament.tournamentType || tournament.tournament_type) ? 'Players' : 'Teams'}
							</button>
							
							{/* Add chat button for unregistered users too */}
							<button
								onClick={() => openChatModal(tournament)}
								className="flex-1 px-3 py-2 bg-green-50 border border-green-200 text-green-700 text-xs font-medium rounded-md hover:bg-green-100 transition flex items-center justify-center relative"
							>
								{/* Red notification dot */}
								<span className={`absolute -top-1 -right-1 h-3 w-3 rounded-full ${isChatDotBlinking ? 'bg-red-500' : 'bg-red-400'} border border-white`}></span>
								<svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
								</svg>
								Chat
							</button>
						</div>

						{/* Show proper button based on tournament status */}
						{(tournament.tournamentStatus === 'completed' || tournament.tournament_status === 'completed') ? (
							<button
								className="flex-1 px-3 py-2 text-xs font-medium rounded-md bg-gray-300 text-gray-700 cursor-not-allowed border border-gray-400 flex items-center justify-center"
								disabled
							>
								<svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
								</svg>
								Tournament Completed
							</button>
						) : (
							<button
								onClick={handleRegistrationClick}
								className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition flex items-center justify-center ${
									['solo', 'squad', 'duo', 'team'].includes(tournament.tournamentType || tournament.tournament_type)
										? 'bg-primary-600 hover:bg-primary-700 text-white border border-primary-700'
										: 'bg-gray-300 text-gray-700 cursor-not-allowed border border-gray-400'
								}`}
							>
								{['solo', 'squad', 'duo', 'team'].includes(tournament.tournamentType || tournament.tournament_type) ? (
									<>
										<svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											{(tournament.tournamentType || tournament.tournament_type) === 'solo' ? (
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
											) : (
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
											)}
										</svg>
										Register Now
									</>
								) : (
									<>
										<svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
										</svg>
										Coming Soon
									</>
								)}
							</button>
						)}
					</div>
				)}
			</div>

			{/* Show winners for completed tournaments */}
			{(tournament.tournamentStatus === 'completed' || tournament.tournament_status === 'completed') && 
			 (tournament.winnerPlayers || tournament.winner_players || []).length > 0 && (
				<div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
					<h4 className="text-xs font-medium text-yellow-800 mb-2 flex items-center">
						<svg className="w-4 h-4 mr-1 text-yellow-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
						</svg>
						<span className="flex-shrink-0">Tournament Winners</span>
					</h4>
					<div className="space-y-1.5">
						{(tournament.winnerPlayers || tournament.winner_players || []).map((winner, index) => {
							// Handle both object and string formats
							const winnerId = typeof winner === 'object' ? winner.user_id : winner;
							const truncatedEmail = typeof winner === 'object' ? getTruncatedEmail(winner.email) : '';
							const gameId = typeof winner === 'object' ? winner.game_id : '';
							const gameUsername = typeof winner === 'object' ? winner.game_username : '';
							const isCurrentUser = winnerId === currentPlayerId;
							const isExpanded = expandedWinners[winnerId || index];
							
							return (
								<div 
									key={winnerId || index} 
									className={`text-xs rounded-md overflow-hidden ${
										isCurrentUser ? 'bg-yellow-100 border border-yellow-200' : 'bg-white border border-gray-200'
									}`}
								>
									{/* Dropdown header */}
									<div 
										className="py-1.5 px-2 flex justify-between items-center cursor-pointer"
										onClick={() => toggleWinnerDropdown(winnerId || index)}
									>
										<div className="flex items-center">
											<span className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-yellow-200 text-yellow-800 mr-2">
												{index + 1}
											</span>
											<span className={`${isCurrentUser ? 'font-medium' : ''} truncate`}>
												{truncatedEmail || `Winner ${index + 1}`}
												{isCurrentUser && <span className="ml-1 text-green-600">(You)</span>}
											</span>
										</div>
										<svg 
											className={`w-4 h-4 text-gray-500 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
											fill="none" 
											viewBox="0 0 24 24" 
											stroke="currentColor"
										>
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
										</svg>
									</div>
									
									{/* Dropdown content */}
									{isExpanded && (gameId || gameUsername) && (
										<div className="bg-gray-50 py-2 px-3 border-t border-gray-200 text-[10px] text-gray-600">
											{gameUsername && (
												<div className="flex justify-between mb-1">
													<span className="font-medium">Username:</span>
													<span>{gameUsername}</span>
												</div>
											)}
											{gameId && (
												<div className="flex justify-between">
													<span className="font-medium">Game ID:</span>
													<span className="font-mono">{gameId}</span>
												</div>
											)}
										</div>
									)}
								</div>
							);
						})}
					</div>
				</div>
			)}

			{/* Transparent overlay for completed tournaments where player is winner and reward not claimed yet */}
			{(tournament.tournamentStatus === 'completed' || tournament.tournament_status === 'completed') && 
			 isCurrentPlayerWinner() && 
			 !(tournament.rewardsClaimed || tournament.rewards_claimed) && (
				<div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center z-10">
					<div className="text-center">
						<div className="mb-3 text-yellow-300 text-xl font-bold animate-bounce">
							🏆 Congratulations! 🏆
						</div>
						
						{/* Improved claim button with more subtle animation */}
						<button
							onClick={handleClaimReward}
							disabled={isClaiming}
							className={`px-6 py-3 text-white font-bold text-lg rounded-lg relative ${
								isClaiming ? 'opacity-50 cursor-not-allowed' : 'hover:bg-yellow-500'
							}`}
							style={{
								background: 'linear-gradient(to right, #FFD700, #FFA500)',
								boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
							}}
						>
							<span className="relative z-10 flex items-center justify-center">
								{isClaiming ? (
									<>
										<svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
											<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
										</svg>
										Claiming...
									</>
								) : (
									<>
										<svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										Claim Your Reward
									</>
								)}
							</span>
						</button>
						<p className="text-white text-sm mt-3">
							Click to receive your tournament prize!
						</p>
					</div>
				</div>
			)}

			{/* Remove the overly complex animation styles and replace with simpler ones */}
			<style jsx>{`
				@keyframes gentle-pulse {
					0% { opacity: 0.9; }
					50% { opacity: 1; }
					100% { opacity: 0.9; }
				}
				button:not(:disabled) {
					animation: gentle-pulse 2s infinite;
				}
				
				@keyframes chat-dot-pulse {
					0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
					50% { transform: scale(1.2); box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.3); }
					100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
				}
				
				.absolute.-top-1.-right-1 {
					animation: chat-dot-pulse 1.5s infinite;
				}
			`}</style>
		</div>
	);
};

export default TournamentCard;
